import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import type { LensCard, LensColumnKind, LensDataset } from '~/types/leLens';

import DatasetMenu from './DatasetMenu';
import DeclareNowBanner from './DeclareNowBanner';
import FieldList from './FieldList';
import Shelves from './Shelves';
import type { PanelField, PanelFieldHover } from './panelTypes';
import type { PanelState } from './usePanelState';

export type DataTabProps = {
  state: PanelState;
  cards: ReadonlyArray<LensCard>;
  datasets: ReadonlyArray<LensDataset>;
  /** Rows for the active card have not arrived yet: skeleton fields, disabled shelves. */
  loading?: boolean;
  /** An insight card answers in prose: none of the shelves or fields apply. */
  isInsight: boolean;
  onNewDataset?: () => void;
  /** Fired only once removal is known to be allowed (no card reads the dataset). */
  onRemoveDataset?: (datasetId: string) => void;
  onFieldHover?: (hover: PanelFieldHover) => void;
  /** Lets the panel hold Escape from closing while a row menu or inline editor is open. */
  onMenuOpenChange?: (open: boolean) => void;
  onRelabelField?: (field: PanelField, label: string) => void;
  onChangeFieldKind?: (field: PanelField, kind: Exclude<LensColumnKind, 'calculated'>) => void;
  onRemoveField?: (field: PanelField) => void;
  /** The Declare-now banner's own action, shared with source details. */
  onDeclareNow?: () => void;
  declaring?: boolean;
  filtersContent?: React.ReactNode;
  sortContent?: React.ReactNode;
  sourceContent?: React.ReactNode;
  sourceOpen?: boolean;
  onSourceToggle?: () => void;
};

/**
 * The Data tab of the card editor: which dataset the card reads, the shelves
 * that say what to show, and the searchable field library beneath them.
 */
export default function DataTab({
  cards,
  datasets,
  declaring,
  isInsight,
  loading,
  onChangeFieldKind,
  onDeclareNow,
  onFieldHover,
  onMenuOpenChange,
  onNewDataset,
  onRelabelField,
  onRemoveDataset,
  onRemoveField,
  filtersContent,
  sortContent,
  sourceContent,
  sourceOpen = false,
  onSourceToggle,
  state,
}: DataTabProps) {
  if (isInsight) {
    return (
      <Typography color="text.secondary">
        This insight has no field selections. Use Chart to change its type or askRichard to revise its narrative.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
      <Box>
        <Stack alignItems="center" direction="row" spacing={1} sx={{ minHeight: 36 }}>
          <Typography color="text.secondary" sx={{ flexShrink: 0, width: 56 }} variant="caption">
            Dataset
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DatasetMenu
              activeDatasetId={state.draft.datasetId}
              cards={cards}
              datasets={datasets}
              disabled={loading}
              onMenuOpenChange={onMenuOpenChange}
              onNew={() => onNewDataset?.()}
              onRemove={(datasetId) => onRemoveDataset?.(datasetId)}
              onSelect={state.switchDataset}
            />
          </Box>
          {sourceContent && (
            <Tooltip title="Source details">
              <IconButton
                aria-controls="lens-editor-source-details"
                aria-expanded={sourceOpen}
                aria-haspopup="dialog"
                aria-label="Source details"
                onClick={onSourceToggle}
                size="small"
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {!state.dataset && (
          <Typography color="text.secondary" sx={{ display: 'block', ml: '64px' }} variant="caption">
            Start with data
          </Typography>
        )}
        {sourceContent && (
          <Dialog
            aria-labelledby="lens-editor-source-details-title"
            fullWidth
            maxWidth="sm"
            onClose={onSourceToggle}
            open={sourceOpen}
            PaperProps={{ 'data-lens-card-settings-dialog': 'true' }}
            scroll="paper"
          >
            <DialogTitle id="lens-editor-source-details-title">Source details</DialogTitle>
            <DialogContent id="lens-editor-source-details">{sourceContent}</DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={onSourceToggle} variant="contained">
                Done
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>

      <Divider />

      <Shelves disabled={loading} onMenuOpenChange={onMenuOpenChange} state={state} />

      <Box sx={{ pt: 0.25 }}>
        {filtersContent}
        {sortContent}
      </Box>

      {state.isUndeclared && <DeclareNowBanner declaring={declaring} onDeclareNow={onDeclareNow} />}

      <Box sx={{ minHeight: 0 }}>
        <FieldList
          cards={cards}
          disabled={loading}
          loading={loading}
          onChangeFieldKind={onChangeFieldKind}
          onFieldHover={onFieldHover}
          onMenuOpenChange={onMenuOpenChange}
          onRelabelField={onRelabelField}
          onRemoveField={onRemoveField}
          scrollMode="page"
          state={state}
        />
      </Box>
    </Stack>
  );
}
